import cn from 'classnames'
import * as React from 'react'
import { Icon } from '../icon'
import { IconButton } from '../icon-button'
import { Typography } from '../typography'
import styles from './Pagination.module.css'
import { PaginationProps } from './Pagination.types'
import { useResponsiveTotalItems } from './useResponsiveTotalItems'
import { DOTS, getPaginationRange } from './utils'

const defaultValuePageLabel = (pageNumber: number) => `page ${pageNumber}`

function Pagination({
  className,
  currentPage,
  totalPages,
  totalPageNumbers,
  goToPreviousPage,
  goToNextPage,
  goToPage,
  previousPageLabel = 'previous page',
  nextPageLabel = 'next page',
  pageLabel = defaultValuePageLabel,
}: PaginationProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const totalItems = useResponsiveTotalItems(totalPageNumbers, containerRef)

  const paginationRange = React.useMemo(() => {
    return getPaginationRange(currentPage, totalPages, totalItems)
  }, [currentPage, totalPages, totalItems])

  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  return (
    <div ref={containerRef} className={cn(styles.paginationWrapper, className)}>
      <div className={styles.root}>
        <IconButton variant="secondary" glyph="ChevronLeft" onClick={goToPreviousPage} disabled={isFirstPage} aria-label={previousPageLabel} size="M" />
        {paginationRange.map((pageNumber: number | string, index: number) => {
          if (pageNumber === DOTS) {
            return (
              <div key={index} className={styles.ellipsis}>
                <Icon glyph="EllipsisHorizontal" size={16} />
              </div>
            )
          }

          return (
            <button
              type="button"
              onClick={() => goToPage(Number(pageNumber))}
              className={cn(styles.pageButton, {
                [styles.pageButtonSelected]: pageNumber === currentPage,
              })}
              key={index}
              aria-label={pageLabel(Number(pageNumber))}
            >
              <Typography variant="body-s-bold-tight" className={styles.pageButtonLabel}>{pageNumber}</Typography>
            </button>
          )
        })}
        <IconButton variant="secondary" glyph="ChevronRight" onClick={goToNextPage} disabled={isLastPage} aria-label={nextPageLabel} size="M" />
      </div>
    </div>
  )
}

export default Pagination
